import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import ViewQuiltOutlinedIcon from '@mui/icons-material/ViewQuiltOutlined';
import GlobalLayout from './GlobalLayout';
import {
    deleteMedia,
    downloadMedia,
    getMediaList,
    getMediaTypes,
    uploadMedia,
    updateMedia,
    DEFAULT_MEDIA_PRIVATE
} from '../services/front_api';

const MEDIA_TYPE_IDS = {
    prefs: 1,
    image: 2,
    pdf_layout: 3,
    document: 4,
    other: 5
};

const MEDIA_TYPES = [
    { value: 'image', label: 'Images', id: MEDIA_TYPE_IDS.image },
    { value: 'pdf_layout', label: 'Layouts PDF', id: MEDIA_TYPE_IDS.pdf_layout },
    { value: 'prefs', label: 'Préférences', id: MEDIA_TYPE_IDS.prefs },
    { value: 'document', label: 'Documents', id: MEDIA_TYPE_IDS.document },
    { value: 'other', label: 'Autres', id: MEDIA_TYPE_IDS.other }
];

const IMAGE_UPLOAD_ACCEPT = '.jpg,.jpeg,.png,.svg,image/jpeg,image/png,image/svg+xml';
const IMAGE_UPLOAD_EXTENSIONS = ['jpg', 'jpeg', 'png', 'svg'];
const IMAGE_UPLOAD_MIME_TYPES = ['image/jpeg', 'image/png', 'image/svg+xml'];

const normalizeTypeText = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const getMediaTypeValueFromLabel = (label) => {
    const normalizedLabel = normalizeTypeText(label);

    if (normalizedLabel.includes('layout')) return 'pdf_layout';
    if (normalizedLabel.includes('pref')) return 'prefs';
    if (normalizedLabel.includes('image')) return 'image';
    if (normalizedLabel.includes('document') || normalizedLabel.includes('doc')) return 'document';
    if (normalizedLabel.includes('autre') || normalizedLabel.includes('other')) return 'other';

    return '';
};

const normalizeCollection = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.media)) return response.media;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.results)) return response.results;
    return [];
};

const normalizeMediaTypes = (response) => normalizeCollection(response)
    .map((mediaType) => {
        const id = Number(mediaType?.id ?? mediaType?.media_type_id ?? mediaType?.value);
        if (!Number.isInteger(id) || id <= 0) return null;

        const label = mediaType?.label || mediaType?.name || `Type #${id}`;

        return {
            ...mediaType,
            id,
            label,
            value: getMediaTypeValueFromLabel(label) || `media_type_${id}`
        };
    })
    .filter(Boolean);

const getMediaId = (media) => media?.id ?? media?.media_id ?? media?.mediaId ?? media?.uuid ?? '';

const getMediaName = (media) => (
    media?.label
    || media?.title
    || media?.name
    || media?.filename
    || media?.file_name
    || media?.original_name
    || media?.originalName
    || (getMediaId(media) ? `Média #${getMediaId(media)}` : 'Média')
);

const getMediaMime = (media) => media?.mime_type || media?.mimeType || media?.content_type || media?.contentType || '';

const getMediaSize = (media) => media?.size ?? media?.file_size ?? media?.fileSize ?? null;

const getMediaUpdatedAt = (media) => media?.updated_at || media?.updatedAt || media?.created_at || media?.createdAt || null;

const normalizePrivateValue = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
        return ['1', 'true', 'yes', 'on', 'private', 'privé'].includes(value.toLowerCase());
    }

    return false;
};

const getMediaPrivate = (media) => normalizePrivateValue(
    media?.private
    ?? media?.is_private
    ?? media?.isPrivate
    ?? media?.private_media
    ?? media?.privateMedia
);

const getUpdatedMediaFromResponse = (response) => {
    if (!response || typeof response !== 'object' || Array.isArray(response)) return null;

    if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        return response.data;
    }

    if (response.media && typeof response.media === 'object' && !Array.isArray(response.media)) {
        return response.media;
    }

    return response;
};

const getMediaTypeId = (media) => (
    media?.media_type_id
    ?? media?.mediaTypeId
    ?? media?.media_type?.id
    ?? media?.mediaType?.id
    ?? null
);

const getExplicitType = (media) => {
    const mediaType = media?.media_type ?? media?.mediaType;

    if (mediaType && typeof mediaType === 'object') {
        return mediaType.label || mediaType.name || mediaType.code || mediaType.slug || mediaType.value || '';
    }

    return mediaType
        || media?.category
        || media?.kind
        || media?.type
        || '';
};

const getMediaTypeValueFromId = (mediaTypeId, mediaTypeOptions = MEDIA_TYPES) => (
    mediaTypeOptions.find((type) => Number(type.id) === Number(mediaTypeId))?.value || ''
);

const getMediaKind = (media, mediaTypeOptions = MEDIA_TYPES) => {
    const explicitType = String(getExplicitType(media)).toLowerCase();
    const mediaTypeValue = getMediaTypeValueFromId(getMediaTypeId(media), mediaTypeOptions);
    const mimeType = String(getMediaMime(media)).toLowerCase();
    const name = String(getMediaName(media)).toLowerCase();

    if (explicitType.includes('layout')) return 'pdf_layout';
    if (explicitType.includes('pref')) return 'prefs';
    if (explicitType.includes('image')) return 'image';
    if (explicitType.includes('document')) return 'document';
    if (mediaTypeValue) return mediaTypeValue;
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType === 'application/json' && name.includes('layout')) return 'pdf_layout';
    if (mimeType === 'application/json' || name.endsWith('.json')) return 'prefs';
    if (mimeType) return 'document';

    return mediaTypeOptions.some((type) => type.value === explicitType) ? explicitType : 'other';
};

const getTypeLabel = (typeValue, mediaTypeOptions = MEDIA_TYPES) => (
    mediaTypeOptions.find((type) => type.value === typeValue)?.label
    || MEDIA_TYPES.find((type) => type.value === typeValue)?.label
    || 'Autre'
);

const isAllowedImageUpload = (file) => {
    if (!file) return false;

    const extension = String(file.name || '').split('.').pop()?.toLowerCase() || '';
    const mimeType = String(file.type || '').toLowerCase();

    return IMAGE_UPLOAD_EXTENSIONS.includes(extension)
        && (!mimeType || IMAGE_UPLOAD_MIME_TYPES.includes(mimeType));
};

const getUploadFileError = (file) => (
    file && !isAllowedImageUpload(file)
        ? 'Seuls les fichiers JPG, PNG ou SVG peuvent être uploadés.'
        : ''
);

const getTypeIcon = (typeValue) => {
    if (typeValue === 'image') return <ImageOutlinedIcon fontSize="small" />;
    if (typeValue === 'pdf_layout') return <ViewQuiltOutlinedIcon fontSize="small" />;
    if (typeValue === 'prefs') return <SettingsOutlinedIcon fontSize="small" />;
    return <InsertDriveFileOutlinedIcon fontSize="small" />;
};

const formatBytes = (size) => {
    const numericSize = Number(size);
    if (!Number.isFinite(numericSize) || numericSize < 0) return 'Taille inconnue';

    const units = ['o', 'Ko', 'Mo', 'Go'];
    let value = numericSize;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }

    return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const formatDate = (dateValue) => {
    if (!dateValue) return 'Date inconnue';

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return 'Date inconnue';

    return new Intl.DateTimeFormat('fr-FR', {
        dateStyle: 'medium',
        timeStyle: 'short'
    }).format(date);
};

const sortMedias = (items) => [...items].sort((firstMedia, secondMedia) => {
    const firstDate = new Date(getMediaUpdatedAt(firstMedia) || 0).getTime();
    const secondDate = new Date(getMediaUpdatedAt(secondMedia) || 0).getTime();

    if (firstDate !== secondDate) {
        return secondDate - firstDate;
    }

    return getMediaName(firstMedia).localeCompare(getMediaName(secondMedia), 'fr', { sensitivity: 'base' });
});

const Media = () => {
    const fileInputRef = useRef(null);

    const [medias, setMedias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [availableMediaTypes, setAvailableMediaTypes] = useState([]);
    const [mediaTypesLoading, setMediaTypesLoading] = useState(true);
    const [mediaTypesError, setMediaTypesError] = useState('');
    const [mediaTypesReload, setMediaTypesReload] = useState(0);
    const [uploadFileValue, setUploadFileValue] = useState(null);
    const [uploadLabel, setUploadLabel] = useState('');
    const [uploadDescription, setUploadDescription] = useState('');
    const [uploadPrivate, setUploadPrivate] = useState(DEFAULT_MEDIA_PRIVATE);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [previewUrls, setPreviewUrls] = useState({});
    const [previewErrors, setPreviewErrors] = useState({});
    const [previewLoadingIds, setPreviewLoadingIds] = useState([]);
    const [mediaToDelete, setMediaToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [privacyUpdatingIds, setPrivacyUpdatingIds] = useState([]);

    const mediaTypeOptions = useMemo(() => (
        availableMediaTypes.length > 0 ? availableMediaTypes : MEDIA_TYPES
    ), [availableMediaTypes]);

    const imageUploadMediaTypeId = useMemo(() => (
        availableMediaTypes.find((type) => type.value === 'image')?.id ?? null
    ), [availableMediaTypes]);

    const uploadTypeMessage = mediaTypesLoading
        ? 'Chargement des types de médias…'
        : mediaTypesError || (!imageUploadMediaTypeId ? 'Le type de média image est indisponible. Cliquez sur Actualiser pour réessayer.' : '');

    const mediaCounts = useMemo(() => {
        return medias.reduce((counts, media) => {
            const type = getMediaKind(media, mediaTypeOptions);
            return {
                ...counts,
                [type]: (counts[type] || 0) + 1
            };
        }, {});
    }, [medias, mediaTypeOptions]);

    const fetchMedias = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const response = await getMediaList();
            setMedias(sortMedias(normalizeCollection(response)));
        } catch (fetchError) {
            console.error('Erreur lors de la récupération des médias', fetchError);
            setMedias([]);
            setError('Erreur lors de la récupération des médias.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;

        const fetchMediaTypeOptions = async () => {
            setMediaTypesLoading(true);
            setMediaTypesError('');
            setAvailableMediaTypes([]);
            try {
                const response = await getMediaTypes({ page: 1, limit: 100, sort: 'ASC', order: 'id' });
                const normalizedMediaTypes = normalizeMediaTypes(response);

                if (!cancelled) {
                    setAvailableMediaTypes(normalizedMediaTypes);
                }
            } catch (mediaTypesError) {
                console.error('Erreur lors de la récupération des types de médias', mediaTypesError);
                if (!cancelled) {
                    setMediaTypesError('Impossible de charger les types de médias. Cliquez sur Actualiser pour réessayer.');
                }
            } finally {
                if (!cancelled) {
                    setMediaTypesLoading(false);
                }
            }
        };

        fetchMediaTypeOptions();

        return () => {
            cancelled = true;
        };
    }, [mediaTypesReload]);

    useEffect(() => {
        fetchMedias();
    }, [fetchMedias]);

    useEffect(() => {
        let cancelled = false;
        const objectUrls = [];
        const imageMedias = medias.filter((media) => (
            getMediaKind(media, mediaTypeOptions) === 'image' && getMediaId(media)
        ));

        setPreviewUrls({});
        setPreviewErrors({});
        setPreviewLoadingIds(imageMedias.map((media) => String(getMediaId(media))));

        imageMedias.forEach(async (media) => {
            const mediaId = getMediaId(media);
            const mediaIdKey = String(mediaId);

            try {
                const blob = await downloadMedia(mediaId);
                const objectUrl = URL.createObjectURL(blob);

                if (cancelled) {
                    URL.revokeObjectURL(objectUrl);
                    return;
                }

                objectUrls.push(objectUrl);
                setPreviewUrls((currentUrls) => ({
                    ...currentUrls,
                    [mediaIdKey]: objectUrl
                }));
            } catch (previewError) {
                console.error(`Erreur lors du chargement de l'aperçu du média ${mediaId}`, previewError);
                if (!cancelled) {
                    setPreviewErrors((currentErrors) => ({
                        ...currentErrors,
                        [mediaIdKey]: true
                    }));
                }
            } finally {
                if (!cancelled) {
                    setPreviewLoadingIds((currentIds) => currentIds.filter((currentId) => currentId !== mediaIdKey));
                }
            }
        });

        return () => {
            cancelled = true;
            objectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
        };
    }, [medias, mediaTypeOptions]);

    const handleUploadFileChange = (event) => {
        const file = event.target.files?.[0] || null;

        const fileError = getUploadFileError(file);
        if (fileError) {
            setUploadFileValue(null);
            setError(fileError);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        setUploadFileValue(file);
        setError('');

        if (file && !uploadLabel) {
            setUploadLabel(file.name);
        }
    };

    const resetUploadForm = () => {
        setUploadFileValue(null);
        setUploadLabel('');
        setUploadDescription('');
        setUploadPrivate(DEFAULT_MEDIA_PRIVATE);
        setUploadProgress(0);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleUpload = async (event) => {
        event.preventDefault();

        if (!uploadFileValue) {
            setError('Sélectionnez un fichier avant de lancer l’upload.');
            return;
        }

        const fileError = getUploadFileError(uploadFileValue);
        if (fileError) {
            setError(fileError);
            return;
        }

        const normalizedMediaTypeId = Number(imageUploadMediaTypeId);
        const knownMediaType = availableMediaTypes.some((type) => type.value === 'image' && type.id === normalizedMediaTypeId);
        if (mediaTypesLoading || !Number.isInteger(normalizedMediaTypeId) || normalizedMediaTypeId <= 0 || !knownMediaType) {
            setError('Le type de média image est introuvable.');
            return;
        }

        setUploading(true);
        setUploadProgress(0);
        setError('');
        setSuccess('');

        try {
            await uploadMedia(
                uploadFileValue,
                {
                    media_type_id: normalizedMediaTypeId,
                    label: uploadLabel || uploadFileValue.name,
                    description: uploadDescription,
                    private: uploadPrivate
                },
                (progressEvent) => {
                    if (!progressEvent.total) return;
                    setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
                }
            );

            resetUploadForm();
            setSuccess('Média ajouté.');
            await fetchMedias();
        } catch (uploadError) {
            console.error('Erreur lors de l’upload du média', uploadError);
            const reason = uploadError.response?.data?.error;
            setError(typeof reason === 'string' && reason ? reason : 'Impossible d’ajouter ce média.');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async () => {
        const mediaId = getMediaId(mediaToDelete);
        if (!mediaId) return;

        setDeleting(true);
        setError('');
        setSuccess('');

        try {
            await deleteMedia(mediaId);
            setMedias((currentMedias) => currentMedias.filter((media) => String(getMediaId(media)) !== String(mediaId)));
            setMediaToDelete(null);
            setSuccess('Média supprimé.');
        } catch (deleteError) {
            console.error(`Erreur lors de la suppression du média ${mediaId}`, deleteError);
            setError('Impossible de supprimer ce média.');
        } finally {
            setDeleting(false);
        }
    };

    const handleTogglePrivate = async (media) => {
        const mediaId = getMediaId(media);
        if (!mediaId) return;

        const mediaIdKey = String(mediaId);
        const nextPrivate = !getMediaPrivate(media);

        setPrivacyUpdatingIds((currentIds) => (
            currentIds.includes(mediaIdKey) ? currentIds : [...currentIds, mediaIdKey]
        ));
        setError('');
        setSuccess('');

        try {
            const response = await updateMedia(mediaId, { private: nextPrivate });
            const updatedMedia = getUpdatedMediaFromResponse(response);

            setMedias((currentMedias) => currentMedias.map((currentMedia) => (
                String(getMediaId(currentMedia)) === mediaIdKey
                    ? {
                        ...currentMedia,
                        ...(updatedMedia || {}),
                        private: nextPrivate
                    }
                    : currentMedia
            )));
            setSuccess(nextPrivate ? 'Média rendu privé.' : 'Média rendu public.');
        } catch (privacyError) {
            console.error(`Erreur lors de la mise à jour de la confidentialité du média ${mediaId}`, privacyError);
            setError('Impossible de modifier la confidentialité de ce média.');
        } finally {
            setPrivacyUpdatingIds((currentIds) => currentIds.filter((currentId) => currentId !== mediaIdKey));
        }
    };

    return (
        <GlobalLayout>
            <Box sx={{ maxWidth: 1580, mx: 'auto', p: { xs: 1, md: 2 } }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                >
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Médias
                        </Typography>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Chip size="small" label={`${medias.length} médias`} />
                            <Chip size="small" label={`${mediaCounts.image || 0} images`} />
                            <Chip size="small" label={`${mediaCounts.pdf_layout || 0} layouts PDF`} />
                            <Chip size="small" label={`${mediaCounts.prefs || 0} prefs`} />
                        </Stack>
                    </Box>

                    <Button
                        variant="outlined"
                        startIcon={<RefreshOutlinedIcon />}
                        onClick={() => {
                            fetchMedias();
                            setMediaTypesReload((current) => current + 1);
                        }}
                        disabled={loading || mediaTypesLoading}
                        sx={{ borderRadius: 1, minWidth: 132 }}
                    >
                        Actualiser
                    </Button>
                </Stack>

                {error && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 2, borderRadius: 1 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}

                <Paper
                    elevation={0}
                    component="form"
                    onSubmit={handleUpload}
                    sx={{ p: 2, mb: 2, border: '1px solid var(--border-color)', borderRadius: 1, bgcolor: 'var(--card-bg)' }}
                >
                    <Stack spacing={1.5}>
                        {uploadTypeMessage && (
                            <Alert severity={mediaTypesLoading ? 'info' : 'error'}>
                                {uploadTypeMessage}
                            </Alert>
                        )}
                        <Stack direction="row" spacing={1} alignItems="center">
                            <UploadFileOutlinedIcon fontSize="small" color="primary" />
                            <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700 }}>
                                Ajouter
                            </Typography>
                        </Stack>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={IMAGE_UPLOAD_ACCEPT}
                            hidden
                            onChange={handleUploadFileChange}
                        />

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 1.1fr) minmax(180px, 1fr) minmax(220px, 1.3fr) auto' },
                                gap: 1.5,
                                alignItems: 'center'
                            }}
                        >
                            <Button
                                type="button"
                                variant="outlined"
                                startIcon={<UploadFileOutlinedIcon />}
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                sx={{ borderRadius: 1, justifyContent: 'flex-start', minHeight: 40, minWidth: 0 }}
                            >
                                <Box
                                    component="span"
                                    sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                >
                                    {uploadFileValue ? uploadFileValue.name : 'Choisir une image'}
                                </Box>
                            </Button>

                            <TextField
                                label="Nom"
                                value={uploadLabel}
                                onChange={(event) => setUploadLabel(event.target.value)}
                                size="small"
                                disabled={uploading}
                            />

                            <TextField
                                label="Description"
                                value={uploadDescription}
                                onChange={(event) => setUploadDescription(event.target.value)}
                                size="small"
                                disabled={uploading}
                            />

                            <FormControlLabel
                                control={(
                                    <Switch
                                        checked={uploadPrivate}
                                        onChange={(event) => setUploadPrivate(event.target.checked)}
                                        disabled={uploading}
                                    />
                                )}
                                label="Privé"
                                sx={{ mx: 0, whiteSpace: 'nowrap' }}
                            />
                        </Box>

                        {uploading && (
                            <LinearProgress
                                variant={uploadProgress > 0 ? 'determinate' : 'indeterminate'}
                                value={uploadProgress}
                            />
                        )}

                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                                type="button"
                                variant="text"
                                onClick={resetUploadForm}
                                disabled={
                                    uploading
                                    || (
                                        !uploadFileValue
                                        && !uploadLabel
                                        && !uploadDescription
                                        && uploadPrivate === DEFAULT_MEDIA_PRIVATE
                                    )
                                }
                                sx={{ borderRadius: 1 }}
                            >
                                Réinitialiser
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileOutlinedIcon />}
                                disabled={uploading || mediaTypesLoading || !imageUploadMediaTypeId || !uploadFileValue}
                                sx={{ borderRadius: 1, boxShadow: 'none' }}
                            >
                                Uploader
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>

                {loading && (
                    <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
                        <CircularProgress size={28} />
                        <Typography variant="body2" color="text.secondary">
                            Chargement...
                        </Typography>
                    </Stack>
                )}

                {!loading && medias.length === 0 && (
                    <Box sx={{ py: 6, textAlign: 'center', color: 'var(--text-secondary)' }}>
                        <InsertDriveFileOutlinedIcon />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Aucun média.
                        </Typography>
                    </Box>
                )}

                {!loading && medias.length > 0 && (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, minmax(0, 1fr))',
                                lg: 'repeat(3, minmax(0, 1fr))',
                                xl: 'repeat(4, minmax(0, 1fr))'
                            },
                            gap: 2
                        }}
                    >
                        {medias.map((media) => {
                            const mediaId = getMediaId(media);
                            const mediaIdKey = String(mediaId);
                            const mediaKind = getMediaKind(media, mediaTypeOptions);
                            const mediaIsImage = mediaKind === 'image';
                            const previewUrl = previewUrls[mediaIdKey];
                            const previewLoading = previewLoadingIds.includes(mediaIdKey);
                            const previewError = previewErrors[mediaIdKey];
                            const mediaIsPrivate = getMediaPrivate(media);
                            const privacyUpdating = privacyUpdatingIds.includes(mediaIdKey);
                            const privacyToggleColors = mediaIsPrivate
                                ? {
                                    bgcolor: 'rgba(185, 28, 28, 0.92)',
                                    hoverBgcolor: 'rgba(153, 27, 27, 0.96)',
                                    borderColor: 'rgba(254, 202, 202, 0.92)'
                                }
                                : {
                                    bgcolor: 'rgba(4, 120, 87, 0.92)',
                                    hoverBgcolor: 'rgba(6, 95, 70, 0.96)',
                                    borderColor: 'rgba(167, 243, 208, 0.92)'
                                };

                            return (
                                <Paper
                                    key={mediaId || getMediaName(media)}
                                    elevation={0}
                                    sx={{
                                        border: '1px solid var(--border-color)',
                                        borderRadius: 1,
                                        bgcolor: 'var(--card-bg)',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        minWidth: 0
                                    }}
                                >
                                    <Box
                                        sx={{
                                            aspectRatio: '16 / 10',
                                            bgcolor: 'var(--bg-modal-header)',
                                            borderBottom: '1px solid var(--border-color)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                            position: 'relative',
                                            color: 'var(--text-secondary)'
                                        }}
                                    >
                                        <Tooltip title={mediaIsPrivate ? 'Rendre public' : 'Rendre privé'}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleTogglePrivate(media)}
                                                    disabled={privacyUpdating || !mediaId}
                                                    aria-label={mediaIsPrivate ? 'Rendre public' : 'Rendre privé'}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 8,
                                                        right: 8,
                                                        zIndex: 1,
                                                        borderRadius: 1,
                                                        bgcolor: privacyToggleColors.bgcolor,
                                                        color: '#fff',
                                                        border: `1px solid ${privacyToggleColors.borderColor}`,
                                                        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.26)',
                                                        backdropFilter: 'blur(4px)',
                                                        '&:hover': {
                                                            bgcolor: privacyToggleColors.hoverBgcolor
                                                        },
                                                        '&.Mui-disabled': {
                                                            bgcolor: privacyToggleColors.bgcolor,
                                                            color: 'rgba(255, 255, 255, 0.72)'
                                                        }
                                                    }}
                                                >
                                                    {privacyUpdating && <CircularProgress size={16} color="inherit" />}
                                                    {!privacyUpdating && mediaIsPrivate && <LockOutlinedIcon fontSize="small" />}
                                                    {!privacyUpdating && !mediaIsPrivate && <LockOpenOutlinedIcon fontSize="small" />}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        {mediaIsImage && previewLoading && <CircularProgress size={24} />}
                                        {mediaIsImage && !previewLoading && previewUrl && (
                                            <Box
                                                component="img"
                                                src={previewUrl}
                                                alt={getMediaName(media)}
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'contain',
                                                    display: 'block'
                                                }}
                                            />
                                        )}
                                        {mediaIsImage && !previewLoading && !previewUrl && (
                                            <Stack alignItems="center" spacing={0.5}>
                                                <ImageOutlinedIcon fontSize="small" />
                                                <Typography variant="caption">
                                                    {previewError ? 'Aperçu indisponible' : 'Aperçu'}
                                                </Typography>
                                            </Stack>
                                        )}
                                        {!mediaIsImage && (
                                            <Stack alignItems="center" spacing={0.5}>
                                                {getTypeIcon(mediaKind)}
                                                <Typography variant="caption">
                                                    Pas d'aperçu
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Box>

                                    <Stack spacing={1.25} sx={{ p: 1.5, flex: 1 }}>
                                        <Stack direction="row" spacing={1} alignItems="flex-start">
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                                                    {getMediaName(media)}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'block' }}>
                                                    {formatBytes(getMediaSize(media))} · {formatDate(getMediaUpdatedAt(media))}
                                                </Typography>
                                            </Box>

                                            <Tooltip title="Supprimer">
                                                <span>
                                                    <IconButton
                                                        onClick={() => setMediaToDelete(media)}
                                                        disabled={deleting}
                                                        size="small"
                                                        sx={{ border: '1px solid var(--border-color)', borderRadius: 1, color: '#dc2626' }}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Stack>

                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                            <Chip size="small" label={getTypeLabel(mediaKind, mediaTypeOptions)} />
                                            {getMediaMime(media) && <Chip size="small" label={getMediaMime(media)} />}
                                        </Stack>
                                    </Stack>
                                </Paper>
                            );
                        })}
                    </Box>
                )}

                <Dialog
                    open={Boolean(mediaToDelete)}
                    onClose={() => (deleting ? null : setMediaToDelete(null))}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>Supprimer le média</DialogTitle>
                    <DialogContent dividers>
                        <Typography variant="body2">
                            {mediaToDelete ? getMediaName(mediaToDelete) : ''}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setMediaToDelete(null)} disabled={deleting} sx={{ borderRadius: 1 }}>
                            Annuler
                        </Button>
                        <Button
                            onClick={handleDelete}
                            color="error"
                            variant="contained"
                            disabled={deleting}
                            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
                            sx={{ borderRadius: 1, boxShadow: 'none' }}
                        >
                            Supprimer
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </GlobalLayout>
    );
};

export default Media;
